import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import FormatResourceStatus from "go-app/components/FormatResourceStatus/FormatResourceStatus";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { ContactApi } from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import ContactContactCard from "./ContactContactCard";
import ContactInfoCard from "./ContactInfoCard";

interface Props {
	contact: ContactApi;
	customFields: CustomFieldTemplateApi[];
}

const ContactPreview: FC<Props> = ({ contact, customFields }) => {
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();
	const history = useHistory();
	const { isMobile } = useWindowSize();

	const onRemove = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await api.organization().removeContact(contact.id);
			history.push(`/${organization.id}/contacts`);
			addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const renderTitle = () => (
		<>
			{contact.name}
			<FormatResourceStatus status={contact.status} />
		</>
	);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.edit", { ns: "lib" }),
			path: `/${organization.id}/contacts/${contact.id}/edit`,
			variant: "light",
		},
	];

	if (contact.status !== "DELETED")
		buttons.push({
			title: t("common.action.remove", { ns: "lib" }),
			action: () => onRemove(),
			dropdown: true,
		});

	const avatar = {
		label: contact.name,
	};

	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.edit", { ns: "lib" }),
			path: `/${organization.id}/contacts/${contact.id}/edit`,
		},
		{
			title: t("common.action.remove", { ns: "lib" }),
			action: () => onRemove(),
			hide: contact.status === "DELETED",
		},
	];

	return (
		<div className="content-preview">
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} avatar={avatar} />
			<div className="content-preview-body">
				<div className="row">
					<div className="col-md-4">
						<ContactContactCard contact={contact} />
						<ContactInfoCard contact={contact} customFields={customFields} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ContactPreview;
